import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  ConfirmDialog,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiGet, apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { amountColumnHeader, formatRialFa, journalHref } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  AccountListResponse,
  ClosingJournalPlanDto,
  FiscalYearListResponse,
  YearEndClosingPreviewResponse,
  YearEndClosingRunDto,
} from '../../lib/types/finance.js';
import { JOURNAL_STATUS_LABEL } from '../../lib/types/finance.js';

const KIND_LABEL: Record<string, string> = {
  year_close_temporary: 'بستن حساب‌های موقت (درآمد و هزینه)',
  year_close_permanent: 'سند اختتامیه',
  year_open: 'سند افتتاحیهٔ سال بعد',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §09 FISCAL PERIODS — year-end (SOT §12.5
 *   "Closing Entry و Opening Entry خودکار … Preview، Validation، Idempotency و
 *   Rollback قبل از Posting")
 * archetype: guided operation — parameters → preview → prepare → status
 * status: PARITY — every figure is server-computed
 *   (`GET …/fiscal-years/:id/closing/preview`). Preparing creates ordinary
 *   draft journals that go through the normal review/approve/post flow on the
 *   journal pages; rollback is offered only while all of them are drafts, and
 *   the server refuses to post them if balances changed after preparation.
 * state reference: ready · loading · blocked (issues) · prepared · permission_denied
 */
export function YearEndClosingPage() {
  const { unit } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [fiscalYearId, setFiscalYearId] = useState('');
  const [nextFiscalYearId, setNextFiscalYearId] = useState('');
  const [retainedEarningsAccountId, setRetainedEarningsAccountId] = useState('');
  const [roots, setRoots] = useState('4,5');
  const [preview, setPreview] = useState<YearEndClosingPreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmRollback, setConfirmRollback] = useState(false);

  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );
  const accounts = useApiGet<AccountListResponse>(slug ? `${base}/api/v1/finance/accounts` : null, [
    slug,
  ]);
  const current = useApiGet<{ readonly run: YearEndClosingRunDto | null }>(
    slug && fiscalYearId ? `${base}/api/v1/finance/fiscal-years/${fiscalYearId}/closing` : null,
    [slug, fiscalYearId, refreshKey],
  );

  const equityAccounts = useMemo(
    () =>
      accounts.status === 'ready'
        ? accounts.data.items
            .filter((a) => a.isPostable && a.status === 'active')
            .sort((a, b) => a.code.localeCompare(b.code))
        : [],
    [accounts],
  );

  if (!slug) return null;

  function selectYear(id: string) {
    setFiscalYearId(id);
    setPreview(null);
    setError(null);
    if (years.status !== 'ready') return;
    const year = years.data.items.find((y) => y.fiscalYearId === id);
    const next = year
      ? years.data.items
          .filter((y) => y.startDate > year.endDate)
          .sort((a, b) => a.startDate.localeCompare(b.startDate))[0]
      : undefined;
    setNextFiscalYearId(next?.fiscalYearId ?? '');
  }

  async function onPreview() {
    setError(null);
    setPreview(null);
    if (!fiscalYearId || !retainedEarningsAccountId) {
      setError('سال مالی و حساب سود و زیان انباشته را انتخاب کنید.');
      return;
    }
    setBusy(true);
    try {
      const query = new URLSearchParams({ retainedEarningsAccountId, temporaryRootDigits: roots });
      if (nextFiscalYearId) query.set('nextFiscalYearId', nextFiscalYearId);
      setPreview(
        await apiGet<YearEndClosingPreviewResponse>(
          `${base}/api/v1/finance/fiscal-years/${fiscalYearId}/closing/preview?${query.toString()}`,
        ),
      );
    } catch (err) {
      setError(bannerMessageFrom(err, 'پیش‌نمایش ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  async function onPrepare() {
    setError(null);
    setBusy(true);
    try {
      await apiPost(`${base}/api/v1/finance/fiscal-years/${fiscalYearId}/closing`, {
        retainedEarningsAccountId,
        ...(nextFiscalYearId ? { nextFiscalYearId } : {}),
        temporaryRootDigits: roots.split(',').map((r) => r.trim()),
      });
      setPreview(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'تهیهٔ اسناد بستن ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  async function onRollback() {
    setConfirmRollback(false);
    setError(null);
    setBusy(true);
    try {
      await apiPost(`${base}/api/v1/finance/fiscal-years/${fiscalYearId}/closing/rollback`, {});
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'برگرداندن ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  async function onCommit() {
    setError(null);
    setBusy(true);
    try {
      await apiPost(`${base}/api/v1/finance/fiscal-years/${fiscalYearId}/closing/commit`, {});
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'قطعی‌سازی اسناد بستن سال ناموفق بود.'));
    } finally {
      setBusy(false);
    }
  }

  const run = current.status === 'ready' ? current.data.run : null;
  const allDraft = run?.journals.every((j) => j.status === 'draft') ?? false;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
        بستن سال: حساب‌های موقت به سود و زیان انباشته بسته می‌شوند، سند اختتامیه حساب‌های دائمی را
        می‌بندد و سند افتتاحیه همان مانده‌ها را در سال بعد باز می‌کند. اسناد به‌صورت پیش‌نویس ساخته
        می‌شوند و مانند هر سند دیگر بررسی، تأیید و قطعی می‌شوند.
      </div>

      <V2Card>
        <V2CardHeader title="بستن سال مالی" />
        <div style={{ padding: '0 20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <V2FormGrid>
            <V2FormField label="سال مالی" required width="medium">
              <select
                className="sk-select"
                value={fiscalYearId}
                onChange={(e) => selectYear(e.target.value)}
              >
                <option value="">انتخاب سال</option>
                {years.status === 'ready' &&
                  years.data.items.map((y) => (
                    <option key={y.fiscalYearId} value={y.fiscalYearId}>
                      {y.name}
                    </option>
                  ))}
              </select>
            </V2FormField>
            <V2FormField
              label="سال مالی بعد"
              width="medium"
              hint="برای سند اختتامیه و افتتاحیه لازم است"
            >
              <select
                className="sk-select"
                value={nextFiscalYearId}
                onChange={(e) => {
                  setNextFiscalYearId(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">— بدون سند افتتاحیه —</option>
                {years.status === 'ready' &&
                  years.data.items
                    .filter((y) => y.fiscalYearId !== fiscalYearId)
                    .map((y) => (
                      <option key={y.fiscalYearId} value={y.fiscalYearId}>
                        {y.name}
                      </option>
                    ))}
              </select>
            </V2FormField>
            <V2FormField
              label="حساب سود و زیان انباشته"
              required
              width="wide"
              hint="حساب قابل ثبت از گروه حقوق صاحبان سهام"
            >
              <select
                className="sk-select"
                value={retainedEarningsAccountId}
                onChange={(e) => {
                  setRetainedEarningsAccountId(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">انتخاب حساب</option>
                {equityAccounts.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField
              label="رقم اول حساب‌های موقت"
              width="short"
              hint="پیش‌فرض: ۴ درآمد، ۵ هزینه"
            >
              <input
                className="sk-input ltr"
                value={roots}
                onChange={(e) => {
                  setRoots(e.target.value.replace(/[^0-9,]/g, ''));
                  setPreview(null);
                }}
              />
            </V2FormField>
          </V2FormGrid>
          <div style={{ display: 'flex', gap: 8 }}>
            <V2Button
              type="button"
              variant="secondary"
              disabled={busy || Boolean(run)}
              onClick={() => void onPreview()}
            >
              پیش‌نمایش و کنترل
            </V2Button>
            <V2Button
              type="button"
              variant="primary"
              disabled={busy || !preview || preview.issues.length > 0 || Boolean(run)}
              onClick={() => void onPrepare()}
            >
              تهیهٔ اسناد بستن (پیش‌نویس)
            </V2Button>
          </div>
          {error ? <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{error}</div> : null}
        </div>
      </V2Card>

      {fiscalYearId && current.status === 'loading' ? <LoadingState /> : null}
      {current.status === 'error' ? (
        <StatePanel
          variant={current.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={current.error.status === 403 ? 'دسترسی ندارید' : 'وضعیت قابل نمایش نیست'}
        />
      ) : null}
      {run ? (
        <V2Card>
          <V2CardHeader
            title="اسناد بستن تهیه شده"
            subtitle={`سود (زیان) سال: ${formatRialFa(run.netIncomeRial.replace('-', ''), unit)}${run.netIncomeRial.startsWith('-') ? ' زیان' : ''}`}
            actions={
              allDraft ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <V2Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={busy}
                    onClick={() => void onCommit()}
                  >
                    تأیید و قطعی‌سازی یکپارچه اسناد
                  </V2Button>
                  <V2Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() => setConfirmRollback(true)}
                  >
                    برگرداندن
                  </V2Button>
                </div>
              ) : (
                <V2Badge tone="info">برای اصلاح، از سند برگشت استفاده کنید</V2Badge>
              )
            }
          />
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th>سند</th>
                  <th>شماره</th>
                  <th>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {run.journals.map((j) => (
                  <tr key={j.journalId}>
                    <td>
                      <Link to={journalHref(base, j.journalId)}>
                        {KIND_LABEL[j.kind] ?? j.kind}
                      </Link>
                    </td>
                    <td className="ltr sk-num">{j.officialDocumentNumber ?? '—'}</td>
                    <td>
                      <V2Badge tone={j.status === 'posted' ? 'success' : 'neutral'}>
                        {j.status && j.status in JOURNAL_STATUS_LABEL
                          ? JOURNAL_STATUS_LABEL[j.status as keyof typeof JOURNAL_STATUS_LABEL]
                          : (j.status ?? '—')}
                      </V2Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 20px 14px', fontSize: 12, color: 'var(--sk-fg-muted)' }}>
            اسناد را به ترتیب جدول ارسال، تأیید و قطعی کنید. اگر پس از تهیه سندی در این سال قطعی
            شود، قطعی‌کردن این اسناد رد می‌شود و باید آن‌ها را برگردانید و دوباره تهیه کنید.
          </div>
        </V2Card>
      ) : null}

      {preview ? (
        <>
          {preview.issues.length > 0 ? (
            <V2Card>
              <V2CardHeader title="موانع بستن سال" />
              <ul style={{ margin: 0, padding: '0 36px 16px', color: 'var(--sk-danger)' }}>
                {preview.issues.map((i) => (
                  <li key={i.code} style={{ fontSize: 13, marginBottom: 4 }}>
                    {i.message}
                  </li>
                ))}
              </ul>
            </V2Card>
          ) : null}
          <V2Card>
            <V2CardHeader
              title="نتیجهٔ سال"
              subtitle={`${BigInt(preview.netIncomeRial) >= 0n ? 'سود' : 'زیان'} خالص: ${formatRialFa(
                (BigInt(preview.netIncomeRial) < 0n
                  ? -BigInt(preview.netIncomeRial)
                  : BigInt(preview.netIncomeRial)
                ).toString(),
                unit,
              )}`}
            />
          </V2Card>
          {[preview.temporaryClosing, preview.permanentClosing, preview.opening].map((p) =>
            p ? <ClosingPlanCard key={p.kind} plan={p} unit={unit} /> : null,
          )}
          {!preview.temporaryClosing && !preview.permanentClosing ? (
            <V2EmptyState icon="info" title="سندی برای تهیه وجود ندارد" />
          ) : null}
        </>
      ) : null}

      {confirmRollback ? (
        <ConfirmDialog
          open
          title="برگرداندن بستن سال؟"
          message="اسناد پیش‌نویس بستن باطل می‌شوند و می‌توانید دوباره پیش‌نمایش و تهیه کنید."
          danger
          confirmLabel="برگرداندن"
          cancelLabel="انصراف"
          onConfirm={() => void onRollback()}
          onCancel={() => setConfirmRollback(false)}
        />
      ) : null}
    </div>
  );
}

function ClosingPlanCard(props: {
  readonly plan: ClosingJournalPlanDto;
  readonly unit: ReturnType<typeof useAmountDisplay>['unit'];
}) {
  const { plan, unit } = props;
  return (
    <V2Card>
      <V2CardHeader
        title={KIND_LABEL[plan.kind] ?? plan.kind}
        subtitle={`${plan.description} · ${displayJalaliDate(plan.businessDate)} · ${plan.lines.length.toLocaleString('fa-IR')} سطر`}
      />
      <div className="sk-table-scroll">
        <table className="sk-table sk-table--compact">
          <thead>
            <tr>
              <th>حساب</th>
              <th>تفصیلی</th>
              <th style={{ width: 140 }}>{amountColumnHeader('بدهکار', unit)}</th>
              <th style={{ width: 140 }}>{amountColumnHeader('بستانکار', unit)}</th>
            </tr>
          </thead>
          <tbody>
            {plan.lines.map((l, i) => (
              <tr key={`${l.accountId}-${i}`}>
                <td>
                  <span className="ltr sk-num" style={{ color: 'var(--sk-fg-muted)' }}>
                    {l.accountRef}
                  </span>{' '}
                  {l.accountName}
                </td>
                <td style={{ fontSize: 12 }}>
                  {l.dimensions.map((d) => `${d.dimensionName}: ${d.valueName}`).join('، ') || '—'}
                </td>
                <td className="sk-num">
                  {l.debitRial !== '0' ? formatRialFa(l.debitRial, unit) : '—'}
                </td>
                <td className="sk-num">
                  {l.creditRial !== '0' ? formatRialFa(l.creditRial, unit) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ fontWeight: 700 }}>
                جمع
              </td>
              <td className="sk-num" style={{ fontWeight: 700 }}>
                {formatRialFa(plan.debitTotalRial, unit)}
              </td>
              <td className="sk-num" style={{ fontWeight: 700 }}>
                {formatRialFa(plan.creditTotalRial, unit)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </V2Card>
  );
}
